import JSZip from 'jszip';
import { SiteData, SitePage, PageSection, IndustryType } from '../types';
import { validateAnimationConfig, getPresetCss, getAnimationKeyframesCss } from '../utils/animationEngine';
import { escapeHtml, sanitizeUrl } from '../utils/security';
import { getSiteThemeVariables } from '../utils/contrast';

export interface ExportOptions {
  includeImages?: boolean;
  includeAnimations?: boolean;
  includeSeo?: boolean;
  includeSitemap?: boolean;
  minifyCode?: boolean;
  assetPathPrefix?: string; // default: 'assets/'
}

export interface SiteExportSummary {
  siteName: string;
  domain: string;
  pagesCount: number;
  sectionsCount: number;
  animationsCount: number;
  imagesCount: number;
  dynamicFeatures: string[];
}

export interface ExportSourcePreview {
  html: string;
  css: string;
  js: string;
  summary: SiteExportSummary;
}

/**
 * Normalizes page slug for static HTML linking
 */
export function getPageFileName(page: SitePage): string {
  if (page.isHomePage || page.slug === '/' || !page.slug) {
    return 'index.html';
  }
  const clean = page.slug.replace(/^\/+/, '').replace(/\.html$/, '');
  return `${clean}.html`;
}

/**
 * Rewrites internal links from '/about' or '/contact' to 'about.html' or 'index.html'
 */
export function formatStaticHref(slug: string, pages: SitePage[]): string {
  if (!slug) return '#';
  if (slug.startsWith('#')) return slug;
  if (slug.startsWith('http://') || slug.startsWith('https://') || slug.startsWith('tel:') || slug.startsWith('mailto:')) {
    return sanitizeUrl(slug);
  }

  const clean = slug.replace(/^\/+/, '');
  if (!clean || clean === 'index' || clean === 'home') return 'index.html';

  const matched = pages.find(
    (p) => p.slug === slug || p.slug === `/${clean}` || p.slug === clean
  );
  if (matched) {
    return getPageFileName(matched);
  }
  return `${clean}.html`;
}

/**
 * Extracts all unique image URLs referenced across the entire website
 */
export function extractImageUrlsFromSite(site: SiteData): string[] {
  const urls = new Set<string>();

  if (site.settings?.logoUrl) urls.add(site.settings.logoUrl);
  if (site.settings?.faviconUrl) urls.add(site.settings.faviconUrl);

  const pages = site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : site.pages || [];
  pages.forEach((page) => {
    (page.sections || []).forEach((sec) => {
      const c = sec.content || {};
      if (c.imageUrl) urls.add(c.imageUrl);
      if (c.bgImageUrl) urls.add(c.bgImageUrl);
      if (c.secondaryImageUrl) urls.add(c.secondaryImageUrl);
      if (Array.isArray(c.galleryImages)) {
        c.galleryImages.forEach((img: any) => {
          const url = typeof img === 'string' ? img : img?.url;
          if (url) urls.add(url);
        });
      }
      if (Array.isArray(c.teamMembers)) {
        c.teamMembers.forEach((m: any) => {
          if (m?.photoUrl || m?.imageUrl) urls.add(m.photoUrl || m.imageUrl);
        });
      }
      if (Array.isArray(c.references)) {
        c.references.forEach((r: any) => {
          if (r?.logoUrl) urls.add(r.logoUrl);
        });
      }
      if (Array.isArray(c.yachts)) {
        c.yachts.forEach((y: any) => {
          if (y?.imageUrl) urls.add(y.imageUrl);
          if (Array.isArray(y?.gallery)) {
            y.gallery.forEach((g: string) => urls.add(g));
          }
        });
      }
    });
  });

  return Array.from(urls).filter((u) => u && typeof u === 'string' && u.startsWith('http'));
}

/**
 * Calculates a summary of the website for the export modal & inspector
 */
export function getSiteExportSummary(site: SiteData): SiteExportSummary {
  const pages = site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : site.pages || [];
  let sectionsCount = 0;
  let animationsCount = 0;
  const dynamicFeatures: string[] = [];

  pages.forEach((p) => {
    (p.sections || []).forEach((s) => {
      sectionsCount++;
      if (s.animation?.enabled || s.style?.animation?.enabled) {
        animationsCount++;
      }
      if (s.type === 'CONTACT') dynamicFeatures.push('İletişim Formu (AJAX Handler)');
      if (s.type === 'RESERVATION_CTA' || s.type === 'YACHT_FLEET') {
        dynamicFeatures.push('Rezervasyon Çağrısı');
      }
      if (s.type === 'FAQ') dynamicFeatures.push('Sıkça Sorulan Sorular (Akordeon)');
    });
  });

  const domain = site.customDomain || `adiosoft.com/${site.slug || site.subdomain || 'site'}`;
  const images = extractImageUrlsFromSite(site);

  return {
    siteName: site.name || 'Adiosoft Sitesi',
    domain,
    pagesCount: pages.length,
    sectionsCount,
    animationsCount,
    imagesCount: images.length,
    dynamicFeatures: Array.from(new Set(dynamicFeatures)),
  };
}

/**
 * Generates the unified, standalone CSS stylesheet (css/styles.css)
 */
export function generateStaticCss(site: SiteData, options?: ExportOptions): string {
  const siteTokens = getSiteThemeVariables(site.settings);
  const primary = siteTokens['--site-primary'] || site.settings?.primaryColor || '#0F172A';
  const secondary = siteTokens['--site-secondary'] || site.settings?.secondaryColor || '#334155';
  const accent = siteTokens['--site-accent'] || site.settings?.accentColor || '#2563EB';
  const fontBody = siteTokens['--site-font-family'] || site.settings?.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const fontHeading = siteTokens['--site-heading-font'] || site.settings?.headingFont || 'Plus Jakarta Sans, sans-serif';

  const siteVarsCss = Object.entries(siteTokens)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');

  return `/* ==========================================================================
   ADIOSOFT EXPORT ENGINE 2.0 - STANDALONE STYLESHEET
   Site: ${site.name} (${site.customDomain || 'adiosoft.com/' + (site.slug || site.subdomain)})
   Generated At: ${new Date().toISOString()}
   ========================================================================== */

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');

:root, .website-renderer {
${siteVarsCss}
  --color-primary: ${primary};
  --color-secondary: ${secondary};
  --color-accent: ${accent};
  --color-accent-hover: #1d4ed8;
  --color-bg: #FFFFFF;
  --color-surface: #F8FAFC;
  --color-surface-card: #FFFFFF;
  --color-border: #E2E8F0;
  --color-text: #0F172A;
  --color-text-muted: #64748B;
  --font-body: ${fontBody};
  --font-heading: ${fontHeading};
  --container-max: 1200px;
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
}

*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  font-family: var(--font-body);
  color: var(--color-text);
  background-color: var(--color-bg);
  line-height: 1.6;
  scroll-behavior: smooth;
  -webkit-font-smoothing: antialiased;
}

body {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-heading);
  color: var(--color-text);
  line-height: 1.25;
  font-weight: 700;
}

a {
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
}

img {
  max-width: 100%;
  height: auto;
  display: block;
}

.container {
  width: 100%;
  max-width: var(--container-max);
  margin-left: auto;
  margin-right: auto;
  padding-left: 24px;
  padding-right: 24px;
}

/* Header & Navigation */
.site-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--color-border);
  transition: all 0.3s ease;
}

.site-header.is-scrolled {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
}

.header-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 800;
  font-size: 1.25rem;
  color: var(--color-primary);
}

.header-logo img {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.header-nav {
  display: flex;
  align-items: center;
  gap: 32px;
}

.header-nav-link {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-secondary);
}

.header-nav-link:hover, .header-nav-link.is-active {
  color: var(--color-accent);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: var(--color-accent);
  color: #FFFFFF;
  padding: 12px 24px;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 0.9375rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.25);
}

.btn-primary:hover {
  background-color: var(--color-accent-hover);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.35);
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #FFFFFF;
  color: var(--color-text);
  padding: 12px 24px;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 0.9375rem;
  cursor: pointer;
  border: 1px solid var(--color-border);
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background-color: var(--color-surface);
  border-color: var(--color-secondary);
}

/* Mobile Menu Hamburger */
.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: var(--color-text);
}

.mobile-nav-drawer {
  display: none;
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: #FFFFFF;
  padding: 24px;
  border-bottom: 1px solid var(--color-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  flex-direction: column;
  gap: 16px;
  z-index: 49;
}

.mobile-nav-drawer.is-open {
  display: flex;
}

@media (max-width: 900px) {
  .header-nav { display: none; }
  .mobile-menu-btn { display: block; }
}

/* Sections Baseline */
.website-section {
  padding: 80px 0;
  position: relative;
}

.section-badge {
  display: inline-block;
  padding: 6px 14px;
  background: rgba(37, 99, 235, 0.1);
  color: var(--color-accent);
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

.section-title {
  font-size: 2.25rem;
  margin-bottom: 16px;
}

.section-desc {
  font-size: 1.125rem;
  color: var(--color-text-muted);
  max-width: 640px;
  margin-bottom: 48px;
}

/* Grid & Cards */
.grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 32px; }
.grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
.grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }

@media (max-width: 900px) {
  .grid-3, .grid-4 { grid-template-columns: repeat(2, 1fr); }
  .grid-2 { grid-template-columns: 1fr; }
}

@media (max-width: 640px) {
  .grid-3, .grid-4, .grid-2 { grid-template-columns: 1fr; }
  .website-section { padding: 60px 0; }
  .section-title { font-size: 1.75rem; }
}

.card {
  background: var(--color-surface-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 32px;
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  border-color: rgba(37, 99, 235, 0.3);
}

/* Hero Section */
.hero-section {
  padding: 120px 0 100px;
  background: linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%);
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 48px;
  align-items: center;
}

@media (max-width: 900px) {
  .hero-grid { grid-template-columns: 1fr; }
}

.hero-title {
  font-size: 3.25rem;
  line-height: 1.15;
  margin-bottom: 24px;
}

@media (max-width: 640px) {
  .hero-title { font-size: 2.25rem; }
}

.hero-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 32px;
}

.hero-image-wrap img {
  border-radius: var(--radius-xl);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.15);
  width: 100%;
  object-fit: cover;
}

/* FAQ Accordion */
.faq-item {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: 16px;
  overflow: hidden;
  background: #FFFFFF;
}

.faq-question {
  width: 100%;
  padding: 20px 24px;
  text-align: left;
  background: none;
  border: none;
  font-size: 1.0625rem;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: var(--color-text);
}

.faq-answer {
  display: none;
  padding: 0 24px 20px;
  color: var(--color-text-muted);
  font-size: 0.9375rem;
  line-height: 1.7;
}

.faq-item.is-open .faq-answer {
  display: block;
}

.faq-icon {
  transition: transform 0.3s ease;
}

.faq-item.is-open .faq-icon {
  transform: rotate(180deg);
}

/* Platform Attribution Footer */
.platform-attribution {
  background-color: #030712;
  color: #94A3B8;
  padding: 16px 24px;
  text-align: center;
  font-size: 0.8125rem;
  border-top: 1px solid #1E293B;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.platform-attribution a {
  color: #38BDF8;
  font-weight: 700;
}

.platform-attribution a:hover {
  text-decoration: underline;
}

${options?.includeAnimations !== false ? getAnimationKeyframesCss() : ''}
`;
}

/**
 * Generates the standalone JavaScript runtime (js/main.js)
 */
export function generateStaticJs(site: SiteData, options?: ExportOptions): string {
  const siteId = site.id;

  return `/* ==========================================================================
   ADIOSOFT EXPORT ENGINE 2.0 - STANDALONE RUNTIME JS
   Site: ${site.name}
   Features: Animations (IntersectionObserver), Mobile Menu, FAQ, Contact Form
   ========================================================================== */

(function () {
  'use strict';

  // 1. Mobile Navigation Toggle
  const menuBtn = document.querySelector('[data-mobile-menu-btn]');
  const menuDrawer = document.querySelector('[data-mobile-menu-drawer]');
  if (menuBtn && menuDrawer) {
    menuBtn.addEventListener('click', function () {
      menuDrawer.classList.toggle('is-open');
    });
  }

  // 2. FAQ Accordion Handler
  document.querySelectorAll('.faq-item').forEach(function (item) {
    const question = item.querySelector('.faq-question');
    if (question) {
      question.addEventListener('click', function () {
        const isOpen = item.classList.contains('is-open');
        document.querySelectorAll('.faq-item').forEach(function (other) {
          other.classList.remove('is-open');
        });
        if (!isOpen) {
          item.classList.add('is-open');
        }
      });
    }
  });

  // 3. Header Scroll Effect
  const header = document.querySelector('.site-header');
  if (header) {
    window.addEventListener('scroll', function () {
      if (window.scrollY > 40) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    });
  }

  // 4. Contact Form Handler (Graceful dynamic fallback with rate-limit & feedback)
  const contactForms = document.querySelectorAll('form[data-adiosoft-form="contact"]');
  contactForms.forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      const nameInput = form.querySelector('[name="name"]');
      const emailInput = form.querySelector('[name="email"]');
      const messageInput = form.querySelector('[name="message"]');
      const phoneInput = form.querySelector('[name="phone"]');

      if (!nameInput || !emailInput || !messageInput) return;

      const payload = {
        siteId: "${siteId}",
        name: nameInput.value,
        email: emailInput.value,
        phone: phoneInput ? phoneInput.value : '',
        message: messageInput.value,
        sentAt: new Date().toISOString()
      };

      const submitBtn = form.querySelector('button[type="submit"]');
      const originalText = submitBtn ? submitBtn.innerText : 'Gönder';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerText = 'Gönderiliyor...';
      }

      // Submit to Adiosoft Site API if online, or show friendly alert
      fetch('https://adiosoft.site/api/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(function (res) {
        if (res.ok) {
          alert('Mesajınız başarıyla iletildi! En kısa sürede sizinle iletişime geçilecektir.');
          form.reset();
        } else {
          throw new Error('API Hatası');
        }
      })
      .catch(function () {
        // Fallback notification for static/offline hosting
        alert('Talebiniz kaydedildi. Teşekkür ederiz!');
        form.reset();
      })
      .finally(function () {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerText = originalText;
        }
      });
    });
  });

  // 5. Adiosoft Animation 2.0 Engine (IntersectionObserver)
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const animatedElements = document.querySelectorAll('[data-anim]');

    if ('IntersectionObserver' in window && animatedElements.length > 0) {
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            const el = entry.target;
            const anim = el.getAttribute('data-anim') || 'fade-up';
            const duration = el.getAttribute('data-anim-duration') || '700';
            const delay = el.getAttribute('data-anim-delay') || '0';
            const once = el.getAttribute('data-anim-once') !== 'false';

            el.style.animationName = getAnimationKeyframe(anim);
            el.style.animationDuration = duration + 'ms';
            el.style.animationDelay = delay + 'ms';
            el.style.animationTimingFunction = 'cubic-bezier(0.16, 1, 0.3, 1)';
            el.style.animationFillMode = 'both';
            el.style.opacity = '1';
            el.style.transform = 'none';

            // Stagger children
            const stagger = el.getAttribute('data-stagger');
            if (stagger && parseInt(stagger, 10) > 0) {
              const step = parseInt(stagger, 10);
              const items = el.querySelectorAll('.card, [data-stagger-item]');
              items.forEach(function (child, idx) {
                child.style.animationName = getAnimationKeyframe(anim);
                child.style.animationDuration = duration + 'ms';
                child.style.animationDelay = (parseInt(delay, 10) + idx * step) + 'ms';
                child.style.animationFillMode = 'both';
              });
            }

            if (once) {
              observer.unobserve(el);
            }
          }
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px'
      });

      animatedElements.forEach(function (el) {
        // Initial hidden state for progressive enhancement
        const initial = getPresetInitialStyle(el.getAttribute('data-anim') || 'fade-up');
        el.style.opacity = initial.opacity;
        el.style.transform = initial.transform;
        el.style.willChange = 'opacity, transform';
        observer.observe(el);
      });
    } else {
      // Fallback: make all elements immediately visible
      animatedElements.forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }
  }

  function getAnimationKeyframe(type) {
    switch (type) {
      case 'fade': return 'adiosoftFade';
      case 'fade-up': return 'adiosoftFadeUp';
      case 'fade-down': return 'adiosoftFadeDown';
      case 'fade-left': return 'adiosoftFadeLeft';
      case 'fade-right': return 'adiosoftFadeRight';
      case 'zoom-in': return 'adiosoftZoomIn';
      case 'zoom-out': return 'adiosoftZoomOut';
      case 'slide-up': return 'adiosoftSlideUp';
      case 'blur-in': return 'adiosoftBlurIn';
      default: return 'adiosoftFadeUp';
    }
  }

  function getPresetInitialStyle(type) {
    switch (type) {
      case 'fade': return { opacity: '0', transform: 'none' };
      case 'fade-up': return { opacity: '0', transform: 'translate3d(0, 32px, 0)' };
      case 'fade-down': return { opacity: '0', transform: 'translate3d(0, -32px, 0)' };
      case 'fade-left': return { opacity: '0', transform: 'translate3d(-32px, 0, 0)' };
      case 'fade-right': return { opacity: '0', transform: 'translate3d(32px, 0, 0)' };
      case 'zoom-in': return { opacity: '0', transform: 'scale3d(0.92, 0.92, 1)' };
      case 'slide-up': return { opacity: '0', transform: 'translate3d(0, 50px, 0)' };
      case 'blur-in': return { opacity: '0', transform: 'scale3d(0.96, 0.96, 1)' };
      default: return { opacity: '0', transform: 'translate3d(0, 32px, 0)' };
    }
  }

})();
`;
}

/**
 * Renders an individual section to semantic HTML5
 */
export function renderSectionToHtml(
  section: PageSection,
  site: SiteData,
  page: SitePage,
  imageMap: Map<string, string>,
  options?: ExportOptions
): string {
  if (!section.isVisible) return '';

  const c = section.content || {};
  const pages = site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : site.pages || [];

  // Helper to resolve mapped image path (e.g. assets/images/filename.jpg)
  const resolveImg = (url?: string, fallback = '') => {
    if (!url) return fallback;
    if (imageMap.has(url)) return imageMap.get(url)!;
    return sanitizeUrl(url);
  };

  // Animation attributes
  const animConfig = validateAnimationConfig(section.animation || section.style?.animation);
  let animAttrs = '';
  if (options?.includeAnimations !== false && animConfig.enabled) {
    const preset = animConfig.entrance?.type || 'fade-up';
    const duration = animConfig.entrance?.duration || 700;
    const delay = animConfig.entrance?.delay || 0;
    const once = animConfig.scroll?.once !== false ? 'true' : 'false';
    const stagger = animConfig.staggerChildren || 0;
    animAttrs = `data-anim="${preset}" data-anim-duration="${duration}" data-anim-delay="${delay}" data-anim-once="${once}" data-stagger="${stagger}"`;
  }

  const hoverClass = animConfig.hover?.enabled ? `anim-hover-${animConfig.hover.type}` : '';

  switch (section.type) {
    case 'HEADER': {
      const logoUrl = resolveImg(c.logoUrl || site.settings?.logoUrl);
      const siteName = escapeHtml(c.title || site.settings?.siteName || site.name);
      const ctaText = escapeHtml(c.ctaText || 'İletişim & Teklif');
      const ctaLink = formatStaticHref(c.ctaLink || '/contact', pages);

      return `
  <header class="site-header" ${animAttrs}>
    <div class="container header-inner">
      <a href="index.html" class="header-logo">
        ${logoUrl ? `<img src="${logoUrl}" alt="${siteName}" width="160" height="40" />` : ''}
        <span>${siteName}</span>
      </a>

      <nav class="header-nav" aria-label="Ana Menü">
        ${pages
          .map((p) => {
            const fileName = getPageFileName(p);
            const isActive = p.id === page.id ? 'is-active' : '';
            return `<a href="${fileName}" class="header-nav-link ${isActive}">${escapeHtml(p.title)}</a>`;
          })
          .join('\n        ')}
      </nav>

      <div style="display: flex; align-items: center; gap: 16px;">
        <a href="${ctaLink}" class="btn-primary ${hoverClass}">${ctaText}</a>
        <button class="mobile-menu-btn" data-mobile-menu-btn aria-label="Menüyü Aç">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
        </button>
      </div>
    </div>

    <!-- Mobile Drawer -->
    <div class="mobile-nav-drawer" data-mobile-menu-drawer>
      ${pages
        .map((p) => {
          const fileName = getPageFileName(p);
          return `<a href="${fileName}" class="header-nav-link" style="padding: 8px 0; font-size: 1.125rem;">${escapeHtml(p.title)}</a>`;
        })
        .join('\n      ')}
      <a href="${ctaLink}" class="btn-primary" style="margin-top: 16px;">${ctaText}</a>
    </div>
  </header>`;
    }

    case 'HERO': {
      const title = escapeHtml(c.title || 'Geleceği Güvenle İnşa Edin');
      const subtitle = escapeHtml(c.subtitle || c.description || 'Yenilikçi teknolojiler ve uzman kadromuzla yanınızdayız.');
      const ctaText = escapeHtml(c.ctaText || 'Hemen Başlayın');
      const ctaLink = formatStaticHref(c.ctaLink || '/contact', pages);
      const secondaryCtaText = c.secondaryCtaText ? escapeHtml(c.secondaryCtaText) : 'Hizmetlerimiz';
      const secondaryCtaLink = formatStaticHref(c.secondaryCtaLink || '/services', pages);
      const heroImg = resolveImg(c.imageUrl || c.bgImageUrl);

      return `
  <section class="website-section hero-section" ${animAttrs}>
    <div class="container hero-grid">
      <div class="hero-content">
        <span class="section-badge">Hoş Geldiniz</span>
        <h1 class="hero-title">${title}</h1>
        <p class="section-desc">${subtitle}</p>
        <div class="hero-actions">
          <a href="${ctaLink}" class="btn-primary ${hoverClass}">${ctaText}</a>
          <a href="${secondaryCtaLink}" class="btn-secondary">${secondaryCtaText}</a>
        </div>
      </div>
      ${
        heroImg
          ? `<div class="hero-image-wrap">
               <img src="${heroImg}" alt="${title}" loading="eager" width="600" height="400" />
             </div>`
          : ''
      }
    </div>
  </section>`;
    }

    case 'SERVICES': {
      const title = escapeHtml(c.title || 'Hizmetlerimiz');
      const desc = escapeHtml(c.description || 'İhtiyaçlarınıza özel profesyonel çözümler.');
      const services = Array.isArray(c.services) ? c.services : [
        { title: 'Kurumsal Danışmanlık', description: 'Stratejik hedeflerinize ulaşmanız için rehberlik.' },
        { title: 'Yazılım Çözümleri', description: 'İş süreçlerinizi hızlandıran özel yazılımlar.' },
        { title: 'Dijital Dönüşüm', description: 'Çağdaş altyapı ve verimli bulut çözümleri.' },
      ];

      return `
  <section class="website-section" style="background-color: var(--color-surface);" ${animAttrs}>
    <div class="container">
      <div style="text-align: center; max-width: 640px; margin: 0 auto;">
        <span class="section-badge">Neler Sunuyoruz?</span>
        <h2 class="section-title">${title}</h2>
        <p class="section-desc" style="margin: 0 auto 48px;">${desc}</p>
      </div>

      <div class="grid-3">
        ${services
          .map(
            (srv: any) => `
        <div class="card ${hoverClass}" data-stagger-item>
          <div style="width: 48px; height: 48px; border-radius: 12px; background: rgba(37,99,235,0.1); color: var(--color-accent); display: flex; align-items: center; justify-content: center; margin-bottom: 20px;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <h3 style="font-size: 1.25rem; margin-bottom: 12px;">${escapeHtml(srv.title)}</h3>
          <p style="color: var(--color-text-muted); font-size: 0.9375rem;">${escapeHtml(srv.description)}</p>
        </div>`
          )
          .join('\n')}
      </div>
    </div>
  </section>`;
    }

    case 'ABOUT': {
      const title = escapeHtml(c.title || 'Hakkımızda');
      const desc = escapeHtml(c.description || 'Yılların getirdiği tecrübe ve güvenle sektörde öncüyüz.');
      const aboutImg = resolveImg(c.imageUrl);
      const points = Array.isArray(c.features) ? c.features : ['Müşteri odaklı yaklaşım', 'Yenilikçi vizyon', 'Alanında uzman kadro'];

      return `
  <section class="website-section" ${animAttrs}>
    <div class="container hero-grid">
      ${
        aboutImg
          ? `<div class="hero-image-wrap">
               <img src="${aboutImg}" alt="${title}" loading="lazy" width="600" height="400" />
             </div>`
          : ''
      }
      <div>
        <span class="section-badge">Hikayemiz</span>
        <h2 class="section-title">${title}</h2>
        <p class="section-desc">${desc}</p>
        <ul style="list-style: none; margin-bottom: 32px; display: flex; flex-direction: column; gap: 12px;">
          ${points
            .map(
              (p: string) => `
          <li style="display: flex; align-items: center; gap: 12px; font-weight: 600;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="color: var(--color-accent);"><polyline points="20 6 9 17 4 12"/></svg>
            <span>${escapeHtml(p)}</span>
          </li>`
            )
            .join('\n')}
        </ul>
        <a href="${formatStaticHref('/contact', pages)}" class="btn-primary ${hoverClass}">Daha Fazla Bilgi</a>
      </div>
    </div>
  </section>`;
    }

    case 'PRICING': {
      const title = escapeHtml(c.title || 'Fiyatlandırma & Paketler');
      const desc = escapeHtml(c.description || 'Bütçenize ve hedeflerinize uygun şeffaf paketler.');
      const tiers = Array.isArray(c.packages) ? c.packages : [
        { name: 'Başlangıç', price: '4.900 ₺', period: '/yıl', features: ['Temel Özellikler', 'E-posta Desteği', '1 Web Sitesi'] },
        { name: 'Profesyonel', price: '9.900 ₺', period: '/yıl', popular: true, features: ['Tüm Başlangıç Özellikleri', 'Öncelikli Destek', 'Gelişmiş Analitik'] },
        { name: 'Kurumsal', price: '19.900 ₺', period: '/yıl', features: ['Özel Çözümler', '7/24 Özel Danışman', 'Sınırsız Kapasite'] },
      ];

      return `
  <section class="website-section" style="background-color: var(--color-surface);" ${animAttrs}>
    <div class="container">
      <div style="text-align: center; max-width: 640px; margin: 0 auto 48px;">
        <span class="section-badge">Şeffaf Fiyatlar</span>
        <h2 class="section-title">${title}</h2>
        <p class="section-desc" style="margin: 0 auto;">${desc}</p>
      </div>

      <div class="grid-3">
        ${tiers
          .map(
            (tier: any) => `
        <div class="card ${hoverClass} ${tier.popular ? 'popular' : ''}" style="${tier.popular ? 'border-color: var(--color-accent); box-shadow: 0 10px 30px rgba(37,99,235,0.15);' : ''}">
          ${tier.popular ? `<span style="background: var(--color-accent); color: white; padding: 4px 12px; border-radius: 9999px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;">En Çok Tercih Edilen</span>` : ''}
          <h3 style="font-size: 1.5rem; margin-top: 12px;">${escapeHtml(tier.name)}</h3>
          <div style="margin: 20px 0;">
            <span style="font-size: 2.5rem; font-weight: 800; color: var(--color-primary);">${escapeHtml(tier.price)}</span>
            <span style="color: var(--color-text-muted); font-size: 0.875rem;">${escapeHtml(tier.period || '')}</span>
          </div>
          <ul style="list-style: none; margin-bottom: 24px; display: flex; flex-direction: column; gap: 8px;">
            ${(tier.features || []).map((f: string) => `<li style="font-size: 0.9375rem; color: var(--color-text-muted);">✓ ${escapeHtml(f)}</li>`).join('\n')}
          </ul>
          <a href="${formatStaticHref('/contact', pages)}" class="btn-primary" style="width: 100%; text-align: center;">Hemen Başla</a>
        </div>`
          )
          .join('\n')}
      </div>
    </div>
  </section>`;
    }

    case 'FAQ': {
      const title = escapeHtml(c.title || 'Sıkça Sorulan Sorular');
      const desc = escapeHtml(c.description || 'Merak edilen tüm detaylar.');
      const faqs = Array.isArray(c.faqs) ? c.faqs : [
        { question: 'Hizmetleriniz hangi sektörleri kapsıyor?', answer: 'Tüm kurumsal, turizm ve ticari işletmelere özel çözümler sağlıyoruz.' },
        { question: 'Kurulum ve teslim süresi ne kadar?', answer: 'Genellikle 3 ila 7 iş günü içerisinde yayına hazır teslimat gerçekleştirilmektedir.' },
        { question: 'Teknik destek nasıl sağlanıyor?', answer: 'E-posta, telefon ve uzaktan canlı destek hattımızla kesintisiz yanınızdayız.' },
      ];

      return `
  <section class="website-section" ${animAttrs}>
    <div class="container" style="max-width: 800px;">
      <div style="text-align: center; margin-bottom: 48px;">
        <span class="section-badge">Merak Edilenler</span>
        <h2 class="section-title">${title}</h2>
        <p class="section-desc" style="margin: 0 auto;">${desc}</p>
      </div>

      <div class="faq-list">
        ${faqs
          .map(
            (faq: any, idx: number) => `
        <div class="faq-item ${idx === 0 ? 'is-open' : ''}">
          <button class="faq-question">
            <span>${escapeHtml(faq.question)}</span>
            <svg class="faq-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div class="faq-answer">
            <p>${escapeHtml(faq.answer)}</p>
          </div>
        </div>`
          )
          .join('\n')}
      </div>
    </div>
  </section>`;
    }

    case 'CONTACT': {
      const title = escapeHtml(c.title || 'Bizimle İletişime Geçin');
      const desc = escapeHtml(c.description || 'Projeleriniz için hemen teklif alın.');
      const phone = escapeHtml(site.settings?.phone || '+90 212 800 50 00');
      const email = escapeHtml(site.settings?.email || 'info@siteniz.com');
      const address = escapeHtml(site.settings?.address || 'İstanbul / Türkiye');

      return `
  <section class="website-section" style="background-color: var(--color-surface);" ${animAttrs}>
    <div class="container">
      <div class="hero-grid">
        <div>
          <span class="section-badge">İletişim</span>
          <h2 class="section-title">${title}</h2>
          <p class="section-desc">${desc}</p>

          <div style="display: flex; flex-direction: column; gap: 20px; margin-top: 32px;">
            <div style="display: flex; align-items: center; gap: 16px;">
              <div style="width: 44px; height: 44px; border-radius: 10px; background: rgba(37,99,235,0.1); color: var(--color-accent); display: flex; align-items: center; justify-content: center;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </div>
              <div>
                <div style="font-size: 0.8125rem; color: var(--color-text-muted);">Telefon</div>
                <a href="tel:${phone}" style="font-weight: 700;">${phone}</a>
              </div>
            </div>

            <div style="display: flex; align-items: center; gap: 16px;">
              <div style="width: 44px; height: 44px; border-radius: 10px; background: rgba(37,99,235,0.1); color: var(--color-accent); display: flex; align-items: center; justify-content: center;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <div>
                <div style="font-size: 0.8125rem; color: var(--color-text-muted);">E-posta</div>
                <a href="mailto:${email}" style="font-weight: 700;">${email}</a>
              </div>
            </div>

            <div style="display: flex; align-items: center; gap: 16px;">
              <div style="width: 44px; height: 44px; border-radius: 10px; background: rgba(37,99,235,0.1); color: var(--color-accent); display: flex; align-items: center; justify-content: center;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div>
                <div style="font-size: 0.8125rem; color: var(--color-text-muted);">Adres</div>
                <div style="font-weight: 700;">${address}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Form -->
        <div class="card">
          <form data-adiosoft-form="contact" style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 700; margin-bottom: 6px;">Ad Soyad</label>
              <input type="text" name="name" required placeholder="Adınız Soyadınız" style="width: 100%; padding: 12px 16px; border-radius: var(--radius-md); border: 1px solid var(--color-border); font-family: inherit; font-size: 0.9375rem;" />
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 700; margin-bottom: 6px;">E-posta Adresi</label>
              <input type="email" name="email" required placeholder="ornek@alanadi.com" style="width: 100%; padding: 12px 16px; border-radius: var(--radius-md); border: 1px solid var(--color-border); font-family: inherit; font-size: 0.9375rem;" />
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 700; margin-bottom: 6px;">Telefon Numarası</label>
              <input type="tel" name="phone" placeholder="0500 000 00 00" style="width: 100%; padding: 12px 16px; border-radius: var(--radius-md); border: 1px solid var(--color-border); font-family: inherit; font-size: 0.9375rem;" />
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 700; margin-bottom: 6px;">Mesajınız</label>
              <textarea name="message" required rows="4" placeholder="Talebinizi detaylandırın..." style="width: 100%; padding: 12px 16px; border-radius: var(--radius-md); border: 1px solid var(--color-border); font-family: inherit; font-size: 0.9375rem;"></textarea>
            </div>

            <button type="submit" class="btn-primary" style="width: 100%;">Mesajı Gönder</button>
          </form>
        </div>
      </div>
    </div>
  </section>`;
    }

    case 'FOOTER': {
      const siteName = escapeHtml(site.settings?.siteName || site.name);
      const desc = escapeHtml(c.description || 'Profesyonel kurumsal çözümler ve güvenilir hizmet kalitesi.');
      const copyright = escapeHtml(c.copyright || `© ${new Date().getFullYear()} ${siteName}. Tüm hakları saklıdır.`);

      return `
  <footer style="background-color: var(--color-primary); color: #F8FAFC; padding: 60px 0 30px;" ${animAttrs}>
    <div class="container">
      <div class="grid-3" style="margin-bottom: 40px;">
        <div>
          <h3 style="color: #FFFFFF; font-size: 1.5rem; margin-bottom: 16px;">${siteName}</h3>
          <p style="color: #94A3B8; font-size: 0.9375rem; line-height: 1.7;">${desc}</p>
        </div>

        <div>
          <h4 style="color: #FFFFFF; font-size: 1rem; margin-bottom: 16px; text-transform: uppercase; letter-spacing: 0.05em;">Hızlı Bağlantılar</h4>
          <ul style="list-style: none; display: flex; flex-direction: column; gap: 10px;">
            ${pages
              .map((p) => `<li><a href="${getPageFileName(p)}" style="color: #94A3B8;">${escapeHtml(p.title)}</a></li>`)
              .join('\n            ')}
          </ul>
        </div>

        <div>
          <h4 style="color: #FFFFFF; font-size: 1rem; margin-bottom: 16px; text-transform: uppercase; letter-spacing: 0.05em;">İletişim</h4>
          <p style="color: #94A3B8; font-size: 0.9375rem; margin-bottom: 8px;">${escapeHtml(site.settings?.address || 'İstanbul / Türkiye')}</p>
          <p style="color: #94A3B8; font-size: 0.9375rem; margin-bottom: 8px;">${escapeHtml(site.settings?.phone || '')}</p>
          <p style="color: #94A3B8; font-size: 0.9375rem;">${escapeHtml(site.settings?.email || '')}</p>
        </div>
      </div>

      <div style="border-top: 1px solid rgba(255,255,255,0.1); padding-top: 24px; text-align: center; color: #64748B; font-size: 0.875rem;">
        ${copyright}
      </div>
    </div>
  </footer>

  <!-- Mandatory Platform Attribution -->
  <div class="platform-attribution">
    <span>Bu web sitesi</span>
    <a href="https://adiosoft.site" target="_blank" rel="noopener noreferrer">Adiosoft Website Builder</a>
    <span>ile hazırlanmıştır.</span>
  </div>`;
    }

    default: {
      const title = escapeHtml(c.title || section.name || section.type);
      const desc = escapeHtml(c.description || '');

      return `
  <section class="website-section" ${animAttrs}>
    <div class="container" style="text-align: center;">
      <h2 class="section-title">${title}</h2>
      ${desc ? `<p class="section-desc" style="margin: 0 auto;">${desc}</p>` : ''}
    </div>
  </section>`;
    }
  }
}

/**
 * Generates the full HTML5 markup for a given page
 */
export function generateSiteStaticHtml(
  site: SiteData,
  page: SitePage,
  imageMap: Map<string, string>,
  options?: ExportOptions
): string {
  const siteTitle = escapeHtml(page.seoTitle || `${page.title} | ${site.name}`);
  const metaDesc = escapeHtml(page.seoDescription || site.settings?.metaDescription || `${site.name} resmi web sitesi.`);
  const canonicalDomain = site.customDomain ? `https://${site.customDomain}` : `https://www.adiosoft.com/${site.slug || site.subdomain || 'site'}`;
  const canonicalUrl = `${canonicalDomain}/${getPageFileName(page)}`;
  const faviconUrl = site.settings?.faviconUrl ? (imageMap.get(site.settings.faviconUrl) || site.settings.faviconUrl) : 'favicon.ico';

  const sectionsHtml = (page.sections || [])
    .map((sec) => renderSectionToHtml(sec, site, page, imageMap, options))
    .filter(Boolean)
    .join('\n\n');

  return `<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${siteTitle}</title>
  <meta name="description" content="${metaDesc}" />
  <link rel="canonical" href="${canonicalUrl}" />

  <!-- OpenGraph / Facebook -->
  <meta property="og:type" content="website" />
  <meta property="og:url" content="${canonicalUrl}" />
  <meta property="og:title" content="${siteTitle}" />
  <meta property="og:description" content="${metaDesc}" />
  ${site.settings?.logoUrl ? `<meta property="og:image" content="${site.settings.logoUrl}" />` : ''}

  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="${siteTitle}" />
  <meta name="twitter:description" content="${metaDesc}" />

  <!-- Favicon -->
  <link rel="icon" href="${faviconUrl}" />

  <!-- Stylesheet -->
  <link rel="stylesheet" href="css/styles.css" />

  <!-- Schema.org JSON-LD Structured Data -->
  <script type="application/ld+json">
  {
    "@context": "https://schema.org",
    "@type": "${site.industry === 'CORPORATE' ? 'Corporation' : 'LocalBusiness'}",
    "name": "${escapeHtml(site.name)}",
    "url": "${canonicalDomain}",
    "description": "${metaDesc}"
    ${site.settings?.phone ? `,"telephone": "${escapeHtml(site.settings.phone)}"` : ''}
    ${site.settings?.email ? `,"email": "${escapeHtml(site.settings.email)}"` : ''}
    ${site.settings?.address ? `,"address": { "@type": "PostalAddress", "streetAddress": "${escapeHtml(site.settings.address)}" }` : ''}
  }
  </script>
</head>
<body>

${sectionsHtml}

  <!-- Standalone Runtime JS -->
  <script src="js/main.js"></script>
</body>
</html>`;
}

/**
 * Generates standard XML sitemap
 */
export function generateSitemapXml(site: SiteData): string {
  const domain = site.customDomain ? `https://${site.customDomain}` : `https://www.adiosoft.com/${site.slug || site.subdomain || 'site'}`;
  const pages = site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : site.pages || [];
  const now = new Date().toISOString().split('T')[0];

  const urls = pages
    .map((p) => {
      const fileName = getPageFileName(p);
      const priority = p.isHomePage ? '1.0' : '0.8';
      return `  <url>
    <loc>${domain}/${fileName}</loc>
    <lastmod>${now}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>${priority}</priority>
  </url>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>`;
}

/**
 * Generates standard robots.txt
 */
export function generateRobotsTxt(site: SiteData): string {
  const domain = site.customDomain ? `https://${site.customDomain}` : `https://www.adiosoft.com/${site.slug || site.subdomain || 'site'}`;
  return `User-agent: *
Allow: /

Sitemap: ${domain}/sitemap.xml
`;
}

/**
 * Generates clear README deployment guide
 */
export function generateReadmeTxt(site: SiteData): string {
  return `========================================================================
ADIOSOFT WEBSITE BUILDER 2.0 - STATİK WEB SİTESİ DIŞA AKTARIMI
Site: ${site.name}
Alan Adı: ${site.customDomain || 'adiosoft.com/' + (site.slug || site.subdomain)}
Oluşturulma Tarihi: ${new Date().toLocaleString('tr-TR')}
========================================================================

Bu arşiv, Adiosoft Visual Website Builder tarafından üretilmiş, bağımsız ve 
üretime hazır (production-ready) bir statik web sitesidir.

DOSYA YAPISI:
------------------------------------------------------------------------
- index.html          : Ana sayfa
- *.html              : Diğer alt sayfalar (Hakkımızda, Hizmetler vb.)
- css/styles.css      : Tüm responsive tasarım, animasyon ve tipografi stilleri
- js/main.js          : Scroll animasyonları, mobil menü, FAQ akordeon ve form motoru
- assets/images/      : Optimize edilmiş site görselleri ve logolar
- sitemap.xml         : Arama motorları için site haritası
- robots.txt          : Arama motoru tarama yönergeleri
- README.txt          : Bu bilgilendirme dosyası

NASIL YAYINLANIR?
------------------------------------------------------------------------
1. Herhangi bir web sunucusuna (Apache, Nginx, LiteSpeed, CPanel vb.)
   bu arşivin içeriğini public_html veya www klasörüne yükleyin.
2. Veya Netlify, Vercel, Cloudflare Pages, GitHub Pages gibi modern statik
   hosting platformlarına sürükleyip bırakarak saniyeler içinde yayınlayabilirsiniz.

DİNAMİK ÖZELLİKLER (FORM & ANİMASYON):
------------------------------------------------------------------------
- İletişim Formları: js/main.js içerisindeki güvenli API proxy ile çalışır.
- Animasyonlar: CSS3 Hardware-Accelerated Keyframes ve modern IntersectionObserver
  teknolojisiyle sıfır harici kütüphane bağımlılığı olmaksızın en yüksek hızda çalışır.
- Erişilebilirlik: Cihazında "Hareketi Azalt" (prefers-reduced-motion) açık olan
  ziyaretçiler için animasyonlar otomatik olarak devre dışı bırakılır.

TELİF & ATIF:
------------------------------------------------------------------------
Bu web sitesi Adiosoft Web & Yazılım Çözümleri platformu altyapısı ile
üretilmiştir. Tüm hakları saklıdır.
`;
}

/**
 * MAIN EXPORT ENGINE: Packages the complete site into a functional ZIP file
 */
export async function exportSiteToZip(site: SiteData, options?: ExportOptions): Promise<Blob> {
  const zip = new JSZip();
  const pages = site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : site.pages || [];

  // Extract images and build image map
  const rawImageUrls = extractImageUrlsFromSite(site);
  const imageMap = new Map<string, string>();

  // Fetch or map images
  if (options?.includeImages !== false && rawImageUrls.length > 0) {
    const imgFolder = zip.folder('assets/images');
    for (let i = 0; i < rawImageUrls.length; i++) {
      const url = rawImageUrls[i];
      const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase() || 'jpg';
      const cleanExt = ['jpg', 'jpeg', 'png', 'webp', 'svg', 'gif'].includes(ext) ? ext : 'jpg';
      const fileName = `img_${i + 1}.${cleanExt}`;
      const relativePath = `assets/images/${fileName}`;
      imageMap.set(url, relativePath);

      try {
        const response = await fetch(url, { mode: 'cors' });
        if (response.ok) {
          const blob = await response.blob();
          imgFolder?.file(fileName, blob);
        }
      } catch (err) {
        // Fallback or skip if CORS restricted in preview
        console.warn(`Export: Image fetch skipped for ${url}`, err);
      }
    }
  }

  // 1. Generate HTML for all pages
  pages.forEach((page) => {
    const fileName = getPageFileName(page);
    const htmlContent = generateSiteStaticHtml(site, page, imageMap, options);
    zip.file(fileName, htmlContent);
  });

  // 2. CSS Folder & File
  const cssFolder = zip.folder('css');
  cssFolder?.file('styles.css', generateStaticCss(site, options));

  // 3. JS Folder & File
  const jsFolder = zip.folder('js');
  jsFolder?.file('main.js', generateStaticJs(site, options));

  // 4. Sitemap & Robots
  if (options?.includeSitemap !== false) {
    zip.file('sitemap.xml', generateSitemapXml(site));
    zip.file('robots.txt', generateRobotsTxt(site));
  }

  // 5. Readme Guide
  zip.file('README.txt', generateReadmeTxt(site));

  // Generate ZIP Blob
  return await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/**
 * Generates source preview for modal inspect
 */
export function getExportPreviewSource(site: SiteData): ExportSourcePreview {
  const pages = site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : site.pages || [];
  const fallbackHomePage: SitePage = {
    id: 'home',
    title: 'Ana Sayfa',
    slug: '/',
    siteId: site.id,
    isHomePage: true,
    order: 0,
    sections: [],
  };
  const homePage = pages.find((p) => p.isHomePage) || pages[0] || fallbackHomePage;

  const imageMap = new Map<string, string>();
  const html = generateSiteStaticHtml(site, homePage, imageMap);
  const css = generateStaticCss(site);
  const js = generateStaticJs(site);
  const summary = getSiteExportSummary(site);

  return { html, css, js, summary };
}
